// src/app/services/performance-series/validate.ts
//
// The trust boundary. Anything coming from the fixture OR the backend passes
// through validateEnvelope() before any chart sees it. The function CANNOT throw
// and CANNOT return malformed data: every value is coerced or dropped, every
// array is sorted and de-collided, and a totally unusable input degrades to a
// valid empty envelope. This is what makes "no broken charts, no data anomalies"
// a structural guarantee rather than a hope.
//
// No React, no `@/` imports — pure and headless-testable.

import type {
  RawEnvelope,
  RawLayer,
  RawSeriesPoint,
  NormalizedEnvelope,
  NormalizedLayer,
  SeriesPoint,
  MemberSeries,
  ValidatedEnvelope,
  ValidationDiagnostic,
} from "./types";

// Same nudge the chart shell uses for same-day runs that serialize to an
// identical epoch: push exact collisions forward so each reading keeps its own
// x position instead of stacking. Done once here so every consumer is safe.
const COLLISION_NUDGE_MS = 60_000;

class Diag {
  readonly items: ValidationDiagnostic[] = [];
  // Cap so a pathological payload can't balloon memory; we only need a sample.
  private static readonly CAP = 200;
  add(code: string, detail: string): void {
    if (this.items.length < Diag.CAP) this.items.push({ code, detail });
  }
}

// ── Primitive coercion ───────────────────────────────────────────────────────

function toFiniteNumber(x: unknown): number | null {
  if (typeof x === "number") return Number.isFinite(x) ? x : null;
  if (typeof x === "string") {
    const t = x.trim();
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function toCleanString(x: unknown): string | null {
  if (typeof x === "string") {
    const t = x.trim();
    return t ? t : null;
  }
  if (typeof x === "number" && Number.isFinite(x)) return String(x);
  return null;
}

/**
 * Parse a timestamp to epoch ms. Accepts ISO strings, epoch ms numbers, epoch
 * seconds numbers (heuristically promoted), and Date instances. Returns null for
 * anything unusable.
 */
function toEpochMs(x: unknown): number | null {
  if (x instanceof Date) {
    const t = x.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof x === "number") {
    if (!Number.isFinite(x)) return null;
    // Heuristic: a value small enough to be seconds (before ~year 2001 in ms)
    // is treated as epoch seconds. Real timestamps are always far larger in ms.
    if (Math.abs(x) < 1e11) return Math.round(x * 1000);
    return Math.round(x);
  }
  if (typeof x === "string") {
    const t = x.trim();
    if (!t) return null;
    // Pure-number string -> recurse through the number path.
    if (/^-?\d+(\.\d+)?$/.test(t)) return toEpochMs(Number(t));
    const ms = Date.parse(t);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

// ── Point / point-array sanitization ─────────────────────────────────────────

function sanitizePoint(raw: unknown, diag: Diag, ctx: string): SeriesPoint | null {
  if (raw == null || typeof raw !== "object") {
    diag.add("drop_point_shape", `${ctx}: point was not an object`);
    return null;
  }
  const r = raw as RawSeriesPoint;
  const t = toEpochMs(r.t);
  if (t == null) {
    diag.add("drop_point_t", `${ctx}: unparseable timestamp`);
    return null;
  }
  const v = toFiniteNumber(r.v);
  if (v == null) {
    diag.add("drop_point_v", `${ctx}: non-finite value at ${new Date(t).toISOString()}`);
    return null;
  }
  const point: SeriesPoint = { t, v };
  const run = toCleanString(r.run);
  if (run) point.run = run;
  const label = toCleanString(r.label);
  if (label) point.label = label;
  return point;
}

/**
 * Sanitize a raw point array into a clean, ascending, collision-free series.
 * Drops bad points, sorts by time, then nudges any exact-or-earlier collision
 * forward so timestamps are strictly increasing.
 */
function sanitizePointArray(raw: unknown, diag: Diag, ctx: string): SeriesPoint[] {
  if (!Array.isArray(raw)) {
    if (raw !== undefined) diag.add("drop_series_shape", `${ctx}: series was not an array`);
    return [];
  }
  const points: SeriesPoint[] = [];
  for (const r of raw) {
    const p = sanitizePoint(r, diag, ctx);
    if (p) points.push(p);
  }
  points.sort((a, b) => a.t - b.t);
  let prev = -Infinity;
  for (const p of points) {
    if (p.t <= prev) {
      const nudged = prev + COLLISION_NUDGE_MS;
      diag.add("nudge_collision", `${ctx}: ${new Date(p.t).toISOString()} -> ${new Date(nudged).toISOString()}`);
      p.t = nudged;
    }
    prev = p.t;
  }
  return points;
}

// ── Layer sanitization ───────────────────────────────────────────────────────

function isPlainObject(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === "object" && !Array.isArray(x);
}

function sanitizeMemberMap(raw: unknown, diag: Diag, ctx: string): MemberSeries {
  const out: MemberSeries = {};
  if (!isPlainObject(raw)) {
    diag.add("drop_dimension_shape", `${ctx}: dimension was not a member map`);
    return out;
  }
  for (const [member, arr] of Object.entries(raw)) {
    const key = member.trim();
    if (!key) {
      diag.add("drop_member_key", `${ctx}: empty member key`);
      continue;
    }
    const pts = sanitizePointArray(arr, diag, `${ctx}.${key}`);
    if (pts.length) out[key] = pts; // omit members with no usable readings
  }
  return out;
}

function sanitizeLayer(raw: unknown, diag: Diag): NormalizedLayer | null {
  if (!isPlainObject(raw)) {
    diag.add("drop_layer_shape", "layer was not an object");
    return null;
  }
  const r = raw as RawLayer;
  const key = toCleanString(r.key);
  if (!key) {
    diag.add("drop_layer_no_key", "layer dropped: missing key");
    return null;
  }
  const label = toCleanString(r.label) ?? key;
  const group = toCleanString(r.group) ?? "other";
  const source = toCleanString(r.source) ?? "unknown";
  const unit = toCleanString(r.unit) ?? "score";

  const series = isPlainObject(r.series) ? r.series : {};
  if (!isPlainObject(r.series)) diag.add("empty_layer_series", `${key}: series missing/!object`);

  // "overall" is a point array; every other key is a member map.
  const overall = sanitizePointArray((series as Record<string, unknown>).overall, diag, `${key}.overall`);
  const members: Record<string, MemberSeries> = {};
  for (const [dim, val] of Object.entries(series as Record<string, unknown>)) {
    if (dim === "overall") continue;
    const dimKey = dim.trim();
    if (!dimKey) continue;
    if (Array.isArray(val)) {
      // A non-overall dimension arriving as a bare array is ambiguous (no member
      // names). Don't invent members; record and skip.
      diag.add("skip_array_dimension", `${key}.${dimKey}: array where member map expected`);
      continue;
    }
    const mm = sanitizeMemberMap(val, diag, `${key}.${dimKey}`);
    if (Object.keys(mm).length) members[dimKey] = mm;
  }

  // Declared dimensions (if provided) inform ordering, but the dimensions we
  // actually expose are only those with real data. "overall" leads when present.
  const declared = Array.isArray(r.dimensions)
    ? r.dimensions.map(toCleanString).filter((s): s is string => !!s)
    : [];

  const present: string[] = [];
  if (overall.length) present.push("overall");
  const memberDims = Object.keys(members);
  // Order member dims by the declared order first, then any extras.
  const ordered = [
    ...declared.filter((d) => d !== "overall" && memberDims.includes(d)),
    ...memberDims.filter((d) => !declared.includes(d)),
  ];
  for (const d of ordered) if (!present.includes(d)) present.push(d);

  return { key, label, group, source, unit, dimensions: present, overall, members };
}

// ── Catalog sanitization ─────────────────────────────────────────────────────

function sanitizeCatalog(raw: unknown, diag: Diag): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  if (!isPlainObject(raw)) {
    if (raw !== undefined) diag.add("drop_catalog_shape", "dimension_catalog was not an object");
    return out;
  }
  for (const [dim, members] of Object.entries(raw)) {
    if (!isPlainObject(members)) continue;
    const dimKey = dim.trim();
    if (!dimKey) continue;
    const map: Record<string, string> = {};
    for (const [member, label] of Object.entries(members)) {
      const mKey = member.trim();
      const lbl = toCleanString(label);
      if (mKey && lbl) map[mKey] = lbl;
    }
    if (Object.keys(map).length) out[dimKey] = map;
  }
  return out;
}

// ── Top-level ────────────────────────────────────────────────────────────────

function emptyEnvelope(brand: string): NormalizedEnvelope {
  return { brand, generatedAt: new Date().toISOString(), layers: [], dimensionCatalog: {} };
}

/**
 * Turn arbitrary input into a guaranteed-safe NormalizedEnvelope. Never throws.
 * `brandHint` is used only to label a fallback when the payload is unusable.
 */
export function validateEnvelope(input: unknown, brandHint = ""): ValidatedEnvelope {
  const diag = new Diag();
  try {
    if (!isPlainObject(input)) {
      diag.add("fallback_root", "envelope was not an object");
      return { envelope: emptyEnvelope(brandHint), diagnostics: diag.items, fellBack: true };
    }
    const r = input as RawEnvelope;
    const brand = toCleanString(r.brand) ?? brandHint;
    const generatedAt = toCleanString(r.generated_at) ?? new Date().toISOString();
    const customerIdNum = toFiniteNumber(r.customer_id);

    const rawLayers = Array.isArray(r.layers) ? r.layers : [];
    if (!Array.isArray(r.layers)) diag.add("layers_missing", "layers missing/!array");

    const layers: NormalizedLayer[] = [];
    const seenKeys = new Set<string>();
    for (const rl of rawLayers) {
      const layer = sanitizeLayer(rl, diag);
      if (!layer) continue;
      if (seenKeys.has(layer.key)) {
        diag.add("drop_duplicate_layer", `duplicate layer key: ${layer.key}`);
        continue;
      }
      // A layer with no overall AND no members carries nothing plottable; keep it
      // out so the lens list never shows an empty chart.
      if (!layer.overall.length && Object.keys(layer.members).length === 0) {
        diag.add("drop_empty_layer", `layer has no data: ${layer.key}`);
        continue;
      }
      seenKeys.add(layer.key);
      layers.push(layer);
    }

    const envelope: NormalizedEnvelope = {
      brand,
      generatedAt,
      layers,
      dimensionCatalog: sanitizeCatalog(r.dimension_catalog, diag),
    };
    if (customerIdNum != null) envelope.customerId = customerIdNum;
    // Incomplete-run verdicts by point run tag. Only well-formed, flagged
    // entries are kept; anything else is dropped, never a reason to fail.
    if (isPlainObject(r.incomplete_runs)) {
      const kept: Record<string, any> = {};
      for (const [tag, verdict] of Object.entries(r.incomplete_runs)) {
        if (isPlainObject(verdict) && verdict.incomplete === true && isPlainObject(verdict.by_lane)) {
          kept[tag] = verdict;
        } else {
          diag.add("drop_incomplete_run", `malformed incomplete_runs entry: ${tag}`);
        }
      }
      if (Object.keys(kept).length) envelope.incompleteRuns = kept;
    }

    return { envelope, diagnostics: diag.items, fellBack: false };
  } catch (err) {
    // Belt-and-suspenders: even an unexpected bug degrades to empty, never a throw.
    diag.add("fallback_exception", String(err));
    return { envelope: emptyEnvelope(brandHint), diagnostics: diag.items, fellBack: true };
  }
}
